/**
 * PATH: packages/ui/src/kanban/components/settings-home/DefaultBoardsSection.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-DEFAULTS-001
 * PURPOSE: All-Kanban default-board pickers for Home Job Quote Finance Email.
 * ROLE IN ARCHITECTURE: Settings-home leaf over useBoardDefaults.
 * MAIN EXPORTS: DefaultBoardsSection
 * NON-RESPONSIBILITIES: Does not set BUILDER (not on the Settings catalog).
 * NOTES FOR FUTURE AI: Reuse the live defaults contract. No new API.
 */

import { useQuery } from '@tanstack/react-query'

import {
  BOARD_DEFAULT_CATEGORY_LABEL,
  type BoardDefaultCategory,
  type KanbanApi,
} from '../../api'
import { useBoardDefaults } from '../../hooks/useBoardDefaults'
import { SectionShell } from './chrome'

const SETTINGS_DEFAULT_CATEGORIES: readonly BoardDefaultCategory[] = [
  'HOME',
  'JOB',
  'QUOTE',
  'FINANCE',
  'EMAIL',
]

export function DefaultBoardsSection({
  api,
  organisationId,
}: {
  api?: KanbanApi
  organisationId?: number
}) {
  const defaults = useBoardDefaults({ api: api as KanbanApi, organisationId })
  const boardsQ = useQuery({
    queryKey: ['kanban', 'boards', organisationId, 'settings-defaults'],
    queryFn: () => api!.listBoards(),
    enabled: Boolean(api?.listBoards) && organisationId !== undefined,
  })

  if (!api || organisationId === undefined) {
    return (
      <SectionShell
        title="Default boards"
        hint="These pickers need the Kanban API and the current organisation."
      >
        <p className="text-xs text-neutral-500" data-testid="settings-default-boards-unavailable">
          Default boards need the Kanban API and the current organisation.
        </p>
      </SectionShell>
    )
  }

  const boards = (boardsQ.data ?? []).filter((board) => !board.archived)

  return (
    <SectionShell
      title="Default boards"
      hint="Landing board per workflow. One board can serve more than one category."
    >
      <div data-testid="settings-default-boards" className="space-y-2">
        {SETTINGS_DEFAULT_CATEGORIES.map((category) => {
          const value = defaults.defaultBoardId(category) ?? ''
          return (
            <label
              key={category}
              className="block text-xs text-neutral-600 dark:text-zinc-300"
            >
              <span className="mb-1 block font-medium">
                {BOARD_DEFAULT_CATEGORY_LABEL[category]}
              </span>
              <select
                data-testid={`settings-default-board-${category.toLowerCase()}`}
                value={value}
                disabled={defaults.isLoading || boardsQ.isLoading}
                onChange={(event) => {
                  const next = event.target.value
                  if (!next) defaults.clearDefault(category)
                  else defaults.setDefault(category, next)
                }}
                className="w-full rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-sm text-neutral-800 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
              >
                <option value="">Not set</option>
                {boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {board.name}
                  </option>
                ))}
              </select>
            </label>
          )
        })}
      </div>
    </SectionShell>
  )
}
